import { GenericAbortSignal } from "axios";
import { MprcRequestErrorReport } from "components";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { FeedType, KnowledgeFeedOrder, QuickFilterDto } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { usePageTitle } from "hooks/usePageTitle";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { FeedContainer } from "views/knowledge/feed/components/FeedContainer";
import { IFeedFilterOptions } from "views/knowledge/feed/components/FeedFilter";
import { getFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { FeedManager, FeedManagerHandle } from "views/knowledge/feed/components/FeedManager";
import { FeedStartupFilter } from "views/knowledge/feed/FeedStartup/FeedStartupFilter";
import { FeedStartupRightPanel } from "views/knowledge/feed/FeedStartup/FeedStartupRightPanel";

export const FeedStartup: React.FC = () => {
    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();

    const { t } = useTranslation("knowledge");
    const [count, setCount] = useState<number | undefined | null>(undefined);
    const [viewError, setViewError] = useState<any>();
    const [emptyDataset, setEmptyDataset] = useState<boolean>(false);
    const [prevPub, setPrevPub] = useState<string | null>(location.state?.publicationId ?? null);
    const [advFilter, setAdvFilter] = useState<string | null>(location.state?.filter ?? null);
    const [expandedAdvFilter, setExpandedAdvFilter] = useState<boolean>(location.state?.expanded ?? false);
    const [showPanel, setShowPanel] = useState<boolean>(true);

    usePageTitle(t("Feed.Startup.PageTitle"));

    ///
    const viewFilter = useRef<IFeedFilterOptions>(
        location.state?.filter
        ?? {
            searchText: "",
            advFilters: [],
            order: KnowledgeFeedOrder.MostRelevant
        } as IFeedFilterOptions
    );

    ///
    const restart = () => {
        setCount(null);
        navigate(document.location.search, { replace: true, state: { filter: viewFilter.current, expanded: expandedAdvFilter } });
    };

    ///
    const applyStateFilter = () => {
        if (location?.state?.filter != null) feedRef.current.reset();
        setAdvFilter(location.state.filter);
    };

    ///
    const handleAdvFilterChange = (filter: IFeedFilterOptions) => {
        viewFilter.current = filter;
        restart();
    };

    ///
    const onGetData = async (startIndex: number, endIndex: number, signal: GenericAbortSignal | undefined) => {
        const filters = getFilter(viewFilter.current);

        var result = await api.v1.knowledgeFeed.getFeedStartup({
            startPublicationId: location.state?.publicationId ?? null,
            startIndex,
            endIndex,
            filters,
            order: viewFilter.current.order,
            searchText: viewFilter.current.searchText ?? null
        }, { signal });

        setCount(result.data.totalRows);

        return result.data.items;
    };

    ///
    const onEmptyDataset = () => {
        setEmptyDataset(viewFilter.current.advFilters.length == 0 && !viewFilter.current.searchText);
    };

    ///
    useDidUpdateEffect(() => {
        if (prevPub != location.state?.publicationId) {
            setPrevPub(null);
            applyStateFilter();
        }
        else if (location.state?.filter && advFilter != location.state.filter) {
            applyStateFilter();
        }
    }, [location.state]);

    ///
    useEffect(() => {
        feedRef.current?.reset();
    }, []);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return emptyDataset
        ? <MprcFilterEmptyDataset message={t("Feed.Startup.EmptyDatasetMessage")} />
        : viewError
            ? <MprcRequestErrorReport
                error={viewError}
                onRetry={() => setViewError(null)}
            />
            : <FeedContainer
                filter={
                    <FeedStartupFilter
                        count={count ?? undefined}
                        filter={viewFilter.current}
                        expandedAdvFilter={expandedAdvFilter}
                        onFilterChange={handleAdvFilterChange}
                        onGetQuickFilterOptions={() =>
                            new Promise<QuickFilterDto[]>((resolve, reject) => {
                                api.v1.knowledgeFeed.getQuickFilterFeedStartup()
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            })
                        }
                        onSelectedExpandedAdvFilter={(selected) => {
                            setExpandedAdvFilter(selected);
                            navigate(document.location.search, { replace: true, state: { expanded: selected } });
                        }}
                        onError={(error) => setViewError(error)}
                    />
                }
                feed={
                    <FeedManager
                        feedType={FeedType.Startup}
                        otherInfo={{ feedType: FeedType.Startup }}
                        onGetData={onGetData}
                        onEmptyDataset={onEmptyDataset}
                        ref={feedRef}
                        useInfiniteLoading
                        previousPublicationId={prevPub}
                        onGobackTop={restart}
                        onError={(error) => setViewError(error)}
                    />
                }
                moreInfo={
                    showPanel && (count ?? 0) > 0
                        ? <FeedStartupRightPanel key="FeedStartupRightPanel" onEmptyPanel={() => setShowPanel(false)} />
                        : undefined
                }
            />;
};
